import type { GridBaseProps } from "@repo/ui";

import { log, PushProcessingNotification, styles } from "@repo/helpers";
import { SortTypeEnum } from "@repo/models";
import {
	BasicDialog,
	Button,
	ButtonColor,
	ButtonTypes,
	Card,
	CardContent,
	CardFooter,
	Column,
	GridPreLoaded,
	OriginalTimezoneDateColumn,
} from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import type { BackgroundCheck, RepSegment } from "../../../models";

import { useBackgroundCheckApi, useBackgroundCheckQueries } from "../../../api/backgroundCheckApi";
import * as Queries from "../../../api/queries";
import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { useRepContext } from "../../../contexts/editableItemContext";
import { useOnboardingContext } from "../../../contexts/onboardingContext";
import { useSegmentContext } from "../../../contexts/segmentContext";
import { useSettingsQuery } from "../../../helpers/queryHooks";
import style from "../../onboardingSessions/style.module.scss";

interface Props extends GridBaseProps<BackgroundCheck> {
	repId: number;
}

const COLUMNS = [
	new OriginalTimezoneDateColumn("Request Date", "requestDate").dateOnly().withSortBy(SortTypeEnum.descending),
	new OriginalTimezoneDateColumn("Response Date", "responseDate").dateOnly(),
	new Column<BackgroundCheck>("Screening Provider").withFormat(
		(b: BackgroundCheck) => b.screeningProviderOther || b.screeningProviderName,
	),
	new Column("Requested By", "requestedByName"),
] as Column<BackgroundCheck>[];

export default function BackgroundCheckGridBasic(props: Props) {
	const { repId } = useRepContext();
	const { useBackgroundCheckQuery } = useBackgroundCheckQueries();
	const { data, loaded, clearQuery } = useBackgroundCheckQuery();
	const backgroundChecks = data?.data ?? ([] as BackgroundCheck[]);
	const [settings] = useSettingsQuery();
	const { backgroundCheckSettings, multiStateEnabled } = settings;
	const expirationDays = backgroundCheckSettings?.expirationDays;
	const { sessionId, session } = useOnboardingContext();
	const { segmentId } = useSegmentContext();
	const { useRepSegmentsQuery, getRepSegment } = useRepSegmentsApi(repId);
	const { data: repSegments, loading: repSegmentsLoading } = useRepSegmentsQuery();

	const [copyWizardData, setCopyWizardData] = useState<{
		visible: boolean;
		backgroundCheckId?: number;
		segments?: RepSegment[];
	}>({ visible: false });

	const columns = useMemo(() => {
		const allColumns = [...COLUMNS];

		if (multiStateEnabled && !repSegmentsLoading)
			allColumns.push(
				new Column("Onboarding State", "onboardingSegmentId").withFormat((id) => getRepSegment(id)?.name),
			);

		if (expirationDays)
			allColumns.push(
				new Column<BackgroundCheck>("Expired?", "expired").withFormat((e) =>
					e ? <span style={{ color: "red" }}>Expired</span> : "Active",
				),
			);

		if (multiStateEnabled && !repSegmentsLoading && repSegments.length > 1) {
			const copyColumn = new Column("Copy", "backgroundCheckId").withFormat((backgroundCheckId) => {
				const { onboardingSegmentId } = backgroundChecks.find(
					(bgc) => bgc.backgroundCheckId === backgroundCheckId,
				);
				const segments = repSegments.filter((rs) => rs.id !== onboardingSegmentId);
				return (
					<Button color={ButtonColor.DarkBlue} onClick={openCopyBackgroundCheckWizard}>
						Copy
					</Button>
				);

				function openCopyBackgroundCheckWizard(e?: React.MouseEvent) {
					e?.stopPropagation();
					e?.preventDefault();
					setCopyWizardData({
						visible: true,
						backgroundCheckId,
						segments,
					});
				}
			});
			allColumns.push(copyColumn);
		}

		return allColumns;
	}, [expirationDays, multiStateEnabled, backgroundChecks, repSegments, repSegmentsLoading]);

	const inSegment = useCallback(
		({ onboardingSegmentId }: BackgroundCheck) => {
			if (!segmentId) return true;
			return segmentId === onboardingSegmentId;
		},
		[segmentId],
	);

	const inSessionOrActive = useCallback(
		({ onboardingSessionId, createdOn, requestDate }: BackgroundCheck) => {
			//if no session selected, show all
			if (!sessionId) return true;
			//if in session, show
			if (sessionId === onboardingSessionId) return true;

			//if not in session, but there is one selected, not included
			if (!expirationDays) return false;
			const bgcDate = new Date(requestDate ?? createdOn ?? new Date());
			const expirationDate = new Date(bgcDate);
			expirationDate.setDate(bgcDate.getDate() + expirationDays);
			const sessionDate = new Date(session.createdOn);
			return sessionDate >= bgcDate && sessionDate < expirationDate;
		},
		[session?.createdOn, sessionId, expirationDays],
	);

	const filteredData = useMemo(() => {
		return backgroundChecks.filter((b) => inSegment(b) && inSessionOrActive(b));
	}, [backgroundChecks, inSessionOrActive, inSegment]);

	return (
		<>
			<GridPreLoaded
				{...props}
				id="bgcGrid"
				pageSize={3}
				columns={columns}
				values={filteredData}
				loading={!loaded}
				getData={clearQuery}
			/>
			<BasicDialog
				title="Copy Background Check"
				open={copyWizardData.visible}
				onOpenChange={(open) => setCopyWizardData((prev) => ({ ...prev, visible: open }))}
				hideTrigger
				className="md:max-w-[800px]"
			>
				{copyWizardData.backgroundCheckId && copyWizardData.segments && (
					<CopyBackgroundCheckWizard
						repSegments={copyWizardData.segments}
						backgroundCheckId={copyWizardData.backgroundCheckId}
						backgroundChecks={backgroundChecks}
					/>
				)}
			</BasicDialog>
		</>
	);
}
interface CopyBackgroundCheckWizardProps {
	repSegments: RepSegment[];
	backgroundCheckId: number;
	backgroundChecks: BackgroundCheck[];
}

function CopyBackgroundCheckWizard({
	repSegments,
	backgroundCheckId,
	backgroundChecks,
}: CopyBackgroundCheckWizardProps) {
	return (
		<section id="Onboarding States" className={style.segmentWizardContainer}>
			<h3>Onboarding States</h3>
			<p>Please select the state you would like to copy the Background Check to.</p>
			<div className={styles(style.cardGrid, style.cardGridColumns)}>
				{repSegments.map(RepSegmentBackgroundCheck)}
			</div>
		</section>
	);

	function RepSegmentBackgroundCheck(repSegment: RepSegment) {
		const { id: repSegmentId, stateName, statusName } = repSegment;
		const numBgc = backgroundChecks.filter((bgc) => bgc.onboardingSegmentId === repSegmentId).length;
		const { copyToNewSegment } = useBackgroundCheckApi();
		const queryClient = useQueryClient();
		const [loading, setLoading] = useState(false);
		const [copied, setCopied] = useState(false);

		const handleCopyClick = async () => {
			setLoading(true);
			const toast = PushProcessingNotification(`Copying Background Check to ${stateName}...`);
			try {
				await copyToNewSegment(backgroundCheckId, repSegmentId);
				await queryClient.invalidateQueries({ queryKey: Queries.BACKGROUND_CHECKS });
				await queryClient.invalidateQueries({ queryKey: Queries.AGENT_PORTAL_TAB_COUNTS });
				await queryClient.invalidateQueries({ queryKey: Queries.ONBOARDING_REQUIREMENTS });
				toast.success(`Successfully copied Background Check to ${stateName}`);
				setCopied(true);
			} catch (e) {
				log(e, "error");
				toast.error(`Failed to copy Background Check to ${stateName}`);
			} finally {
				setLoading(false);
			}
		};

		return (
			<Card key={repSegmentId} className={style.cardContainer}>
				<CardContent>
					<dl className={style.gridItem}>
						<dt>State</dt>
						<dd>
							{stateName} ({statusName})
						</dd>
						<dt>Existing Background Checks</dt>
						<dd>{copied ? numBgc + 1 : numBgc}</dd>
					</dl>
				</CardContent>
				<CardFooter>
					<CopyButton />
				</CardFooter>
			</Card>
		);

		function CopyButton() {
			return (
				<div className={style.flexRow}>
					<Button types={[ButtonTypes.Outline]} loading={loading} disabled={copied} onClick={handleCopyClick}>
						<CopyButtonText />
					</Button>
				</div>
			);

			function CopyButtonText() {
				if (loading) return `Copying To ${stateName}...`;
				if (copied) return <span className={style.textGreen}>Copied To {stateName}!</span>;
				return `Copy To ${stateName}`;
			}
		}
	}
}
